import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export type ToastVariant = 'info' | 'success' | 'danger' | 'warning';

export type ToastRequest = {
  id?: number;
  title?: string;
  message: string;
  variant?: ToastVariant;
  durationMs?: number;
};

type ToastInternal = ToastRequest & { id: number; createdAt: number };

type ToastContextValue = {
  pushToast: (toast: ToastRequest) => void;
};

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

const listeners = new Set<(toast: ToastRequest) => void>();

export function emitToast(toast: ToastRequest) {
  listeners.forEach((listener) => listener(toast));
}

type ToastProviderProps = {
  children: React.ReactNode;
};

const DEFAULT_DURATION = 6000;

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastInternal[]>([]);

  const pushToast = useCallback((toast: ToastRequest) => {
    setToasts((prev) => {
      const id = toast.id ?? Date.now() + Math.floor(Math.random() * 1000);
      const next: ToastInternal = {
        id,
        createdAt: Date.now(),
        title: toast.title,
        message: toast.message,
        variant: toast.variant ?? 'info',
        durationMs: toast.durationMs ?? DEFAULT_DURATION,
      };
      return [...prev, next];
    });
  }, []);

  useEffect(() => {
    const listener = (toast: ToastRequest) => pushToast(toast);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, [pushToast]);

  useEffect(() => {
    if (toasts.length === 0) return;
    const timers = toasts.map((toast) =>
      window.setTimeout(() => {
        setToasts((prev) => prev.filter((item) => item.id !== toast.id));
      }, toast.durationMs ?? DEFAULT_DURATION)
    );
    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [toasts]);

  const value = useMemo<ToastContextValue>(() => ({ pushToast }), [pushToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-container position-fixed top-0 end-0 p-3" style={{ zIndex: 2000 }}>
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`toast text-bg-${variantToBootstrap(toast.variant)} show mb-2 shadow`}
            role="alert"
            aria-live="assertive"
            aria-atomic="true"
          >
            {toast.title ? (
              <div className="toast-header">
                <strong className="me-auto">{toast.title}</strong>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setToasts((prev) => prev.filter((item) => item.id !== toast.id))}
                  aria-label="Close"
                />
              </div>
            ) : null}
            <div className={`toast-body ${toast.title ? '' : 'd-flex justify-content-between align-items-start'}`}>
              <span>{toast.message}</span>
              {!toast.title ? (
                <button
                  type="button"
                  className="btn-close btn-close-white ms-3"
                  onClick={() => setToasts((prev) => prev.filter((item) => item.id !== toast.id))}
                  aria-label="Close"
                />
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function variantToBootstrap(variant?: ToastVariant) {
  switch (variant) {
    case 'success':
      return 'success';
    case 'danger':
      return 'danger';
    case 'warning':
      return 'warning';
    default:
      return 'primary';
  }
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
